<template>
  <div ref="root" class="relative">
    <!-- Mobile / tablet-vertical: tap target that opens the full-screen search -->
    <div
      class="flex h-[38px] w-full flex-row-reverse items-center rounded-sm bg-gray-800/5 px-3 py-2 tablet-h:hidden"
      @click="mobileSearchStore.showSearch()"
    >
      <input
        type="text"
        readonly
        placeholder="Search"
        class="pointer-events-none w-full bg-transparent pr-4 font-primary text-[14px] leading-[18px] text-gray-800 outline-hidden placeholder:text-gray-800/50"
      />
      <SearchIcon
        class="mr-2.5 size-[14px] shrink-0 text-gray-800 opacity-40"
      />
    </div>

    <!-- Desktop: live search field -->
    <Search
      v-model="query"
      size="lg"
      class="hidden tablet-h:block"
      @focus="inputFocused = true"
      @keydown.esc="handleBlur"
    />

    <div
      v-if="inputFocused && (showResults || query.length === 1)"
      class="search--results absolute left-0 top-[calc(100%+3px)] z-100 w-full overflow-hidden rounded-[10px] bg-white"
    >
      <DesktopSearchResults
        v-if="showResults"
        :query="query"
        @navigate="handleClick"
      />
      <div
        v-else
        class="flex h-[60px] w-full items-center justify-center text-[15px] leading-[18px] text-gray-500"
      >
        Your search must be at least 2 characters long
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import DesktopSearchResults from '@/features/search/components/desktop/DesktopSearchResults.vue'
import { SearchIcon } from '@dacr/icons'
import { Search } from '@dacr/library'
import { useMobileSearchStore } from '@/features/search/store'

const mobileSearchStore = useMobileSearchStore()

const query = ref('')
const inputFocused = ref(false)
const root = ref<HTMLElement | null>(null)

const showResults = computed(() => inputFocused.value && query.value.length > 1)

const handleBlur = () => {
  inputFocused.value = false
  ;(document.activeElement as HTMLElement | null)?.blur()
}

const handleClick = () => {
  handleBlur()
  query.value = ''
}

const handleOutsideClick = (event: Event) => {
  if (root.value && !root.value.contains(event.target as Node)) {
    inputFocused.value = false
  }
}

onMounted(() => {
  window.addEventListener('click', handleOutsideClick)
})

onBeforeUnmount(() => {
  window.removeEventListener('click', handleOutsideClick)
})
</script>

<style scoped>
@media (min-width: 1023px) {
  .search--results {
    box-shadow:
      0 0 8px #0000001a,
      0 2px 4px #00000033;
  }
}
</style>
